import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import RecipeCard from "@/components/RecipeCard";
import { fetchRecipes } from "@/lib/api";

const CATEGORIES = ["All", "Chicken", "Seafood", "Snacks", "Veggies", "Desserts", "Quick Meals"];

export default function RecipesPage() {
  const [params, setParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const activeCategory = params.get("category") || "All";

  useEffect(() => {
    document.title = "Recipes — JustAirFry";
  }, []);

  useEffect(() => {
    setLoading(true);
    const apiParams = {};
    if (activeCategory && activeCategory !== "All") {
      if (activeCategory === "Quick Meals") apiParams.quick = true;
      else apiParams.category = activeCategory;
    }
    fetchRecipes(apiParams)
      .then(setRecipes)
      .catch(() => setRecipes([]))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipes;
    return recipes.filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.category.toLowerCase().includes(q)
    );
  }, [recipes, search]);

  const setCategory = (cat) => {
    const next = new URLSearchParams(params);
    if (cat === "All") next.delete("category");
    else next.set("category", cat);
    setParams(next);
  };

  return (
    <div data-testid="recipes-page">
      {/* Header */}
      <section
        className="py-14 md:py-20"
        style={{ backgroundColor: "var(--jaf-surface)" }}
      >
        <div className="jaf-container max-w-4xl">
          <span
            className="inline-block text-xs tracking-[0.18em] uppercase font-semibold mb-3"
            style={{ color: "var(--jaf-primary)" }}
          >
            The recipe library
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            Every recipe in the kitchen
          </h1>
          <p
            className="mt-5 text-base sm:text-lg max-w-2xl leading-relaxed"
            style={{ color: "var(--jaf-text-muted)" }}
          >
            Browse our growing collection of tested, real-kitchen air fryer recipes. Filter by category or search for whatever you're craving.
          </p>

          <div className="mt-8 relative max-w-xl">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: "var(--jaf-text-muted)" }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search recipes (e.g. salmon, fries, donuts)..."
              data-testid="recipes-search-input"
              className="w-full h-12 pl-11 pr-4 rounded-full bg-white outline-none focus:ring-2 text-sm"
              style={{
                border: "1px solid var(--jaf-border)",
                color: "var(--jaf-text)",
              }}
            />
          </div>
        </div>
      </section>

      {/* Filter tabs */}
      <section className="py-8 sticky top-16 z-30" style={{ backgroundColor: "var(--jaf-bg)", borderBottom: "1px solid var(--jaf-border)" }}>
        <div className="jaf-container">
          <div className="flex gap-2 overflow-x-auto pb-2 horizontal-scroll" data-testid="category-tabs">
            {CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  data-testid={`tab-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex-shrink-0 px-5 h-10 rounded-full text-sm font-medium transition-colors"
                  style={
                    isActive
                      ? { backgroundColor: "var(--jaf-primary)", color: "#fff" }
                      : {
                          backgroundColor: "var(--jaf-surface)",
                          color: "var(--jaf-text)",
                          border: "1px solid var(--jaf-border)",
                        }
                  }
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recipes grid */}
      <section className="py-12 md:py-16">
        <div className="jaf-container">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl aspect-[4/5] animate-pulse"
                  style={{ backgroundColor: "var(--jaf-surface)" }}
                />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="no-results">
              <p className="font-serif text-2xl mb-2">No recipes matched that.</p>
              <p style={{ color: "var(--jaf-text-muted)" }}>Try another category or a different search term.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="recipes-grid">
              {filtered.map((r) => (
                <RecipeCard key={r.id} recipe={r} testIdPrefix="recipe-card" />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
